import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { reduce } from '@atlaskit/adf-utils/reduce';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

export type ContentFeatures = {
	rawLength: number;
	textLength: number;
	wordCount: number;
	imageCount: number;
	inlineMacroCount: number;
	macroCount: number;
	tableCount: number;
	mediaFiles: string[];
	looms: { id: string; url: string }[];
};

// helper function to traverse ADF tree while skipping expand nodes
const reduceSkipExpand = <T>(
	node: ADFEntity,
	reducer: (acc: T, node: ADFEntity) => T,
	initialValue: T,
): T => {
	// skip if this is an expand node
	if (node.type === 'expand' || node.type === 'nestedExpand') {
		return initialValue;
	}

	// process current node
	let result = reducer(initialValue, node);

	// process child nodes if they exist
	if (node.content) {
		result = (node.content as ADFEntity[]).reduce(
			(acc, child) => reduceSkipExpand(child, reducer, acc),
			result,
		);
	}

	return result;
};

const wordCount = (text?: string) => {
	if (text) {
		const matches = text.match(/\S+/g);
		if (matches) {
			return matches.length;
		}
	}
	return 0;
};

const processADFNode = (currentValue: ContentFeatures, node: ADFEntity): ContentFeatures => {
	switch (node.type) {
		case 'text':
			return {
				...currentValue,
				textLength: currentValue.textLength + (node.text ? node.text.length : 0),
				wordCount: currentValue.wordCount + wordCount(node.text),
			};
		case 'media':
			return {
				...currentValue,
				imageCount: currentValue.imageCount + 1,
				mediaFiles:
					node.attrs?.type === 'file' && node.attrs?.id
						? currentValue.mediaFiles.concat(node.attrs.id)
						: currentValue.mediaFiles,
			};
		case 'inlineExtension':
			return {
				...currentValue,
				inlineMacroCount: currentValue.macroCount + 1,
			};
		case 'extension':
			return {
				...currentValue,
				macroCount: currentValue.macroCount + 1,
			};
		case 'table':
			return {
				...currentValue,
				tableCount: currentValue.tableCount + 1,
			};
		case 'embedCard':
			const url = node.attrs?.url;

			if (!url.includes('loom')) {
				return currentValue;
			}

			const videoId = url?.split('/').pop().split('?')[0];

			return {
				...currentValue,
				looms: videoId ? currentValue.looms.concat({ id: videoId, url }) : currentValue.looms,
			};
	}
	return currentValue;
};

export const extractContentFeatures = (adf: JSONDocNode): ContentFeatures => {
	const initialFeatures: ContentFeatures = {
		rawLength: JSON.stringify(adf).length,
		textLength: 0,
		wordCount: 0,
		imageCount: 0,
		inlineMacroCount: 0,
		macroCount: 0,
		tableCount: 0,
		mediaFiles: [],
		looms: [],
	};

	return reduce<ContentFeatures>(
		adf,
		(currentValue, node: ADFEntity) => processADFNode(currentValue, node),
		initialFeatures,
	);
};

// Extract content features while skipping expand nodes
export const extractContentFeaturesSkipExpand = (adf: JSONDocNode): ContentFeatures => {
	const initialFeatures: ContentFeatures = {
		rawLength: JSON.stringify(adf).length,
		textLength: 0,
		wordCount: 0,
		imageCount: 0,
		inlineMacroCount: 0,
		macroCount: 0,
		tableCount: 0,
		mediaFiles: [],
		looms: [],
	};

	return reduceSkipExpand<ContentFeatures>(
		adf,
		(currentValue, node: ADFEntity) => processADFNode(currentValue, node),
		initialFeatures,
	);
};
